import { YEAR_PATTERN } from '#shared/constants'
import { useRouteQuery } from '@vueuse/router'
import { extractId } from '#shared/utils/solr-facet'
import { labelToNodeTypes } from '~/utils/solr/search-utils'

export interface CheckboxOption { value: string, label: string, disabled?: boolean, count?: number }

const arrayFilter = {
  transform: {
    get: (val: string | string[] | null | undefined): string[] => {
      if (!val) {
        return []
      }

      if (Array.isArray(val)) {
        return val
      }

      try {
        const parsed = JSON.parse(val)
        return Array.isArray(parsed) ? parsed : []
      }
      catch {
        return []
      }
    },
    set: (val: string[]): string | undefined => val.length ? JSON.stringify(val) : undefined,
  },
} as const

const sortFilter = {
  transform: {
    get: (val: string | null) => {
      const sortVal = val || 'relevance'
      return SORT_ORDER_OPTIONS.find(opt => opt.value === sortVal) || SORT_ORDER_OPTIONS[0]
    },
    set: (val: { value: string, label: string }): string => {
      return val.value === 'relevance' ? '' : val.value
    },
  },
} as const

export function useSearchFilters(options: { resetPageOnFilterChange?: boolean } = {}) {
  const selectedContentTypes = useRouteQuery('contentTypes', [], arrayFilter)
  const selectedLegalCategories = useRouteQuery('legalAreas', [], arrayFilter)
  const selectedAuthors = useRouteQuery('authors', [], arrayFilter)
  const startYearRoute = useRouteQuery<string | undefined>('startYear', undefined)
  const endYearRoute = useRouteQuery<string | undefined>('endYear', undefined)
  const sortOrder = useRouteQuery('sort', '', sortFilter)
  const page = options.resetPageOnFilterChange ? useRouteQuery<number>('page', 1) : undefined

  function resetPageAfterFilterChange() {
    if (page && page.value !== 1) {
      page.value = 1
    }
  }

  // Local date refs to manage input state before applying to route query
  const startYear = ref(startYearRoute.value ?? '')
  const endYear = ref(endYearRoute.value ?? '')

  watch(startYearRoute, val => startYear.value = val ?? '')
  watch(endYearRoute, val => endYear.value = val ?? '')

  function updateYearFilter() {
    // Only update route query if the input is valid and have 4 digits
    if (startYear.value && !YEAR_PATTERN.test(startYear.value)) {
      return
    }
    if (endYear.value && !YEAR_PATTERN.test(endYear.value)) {
      return
    }

    const nextStartYear = startYear.value || undefined
    const nextEndYear = endYear.value || undefined
    if (nextStartYear === startYearRoute.value && nextEndYear === endYearRoute.value) {
      return
    }

    startYearRoute.value = nextStartYear
    endYearRoute.value = nextEndYear
    resetPageAfterFilterChange()
  }

  const searchLegalCategory = ref<string>('')
  const searchAuthor = ref<string>('')

  function toggler(array: Ref<string[]>) {
    return (item: string, checked: boolean) => {
      if (checked) {
        if (!array.value.includes(item)) {
          array.value = [...array.value, item]
          resetPageAfterFilterChange()
        }
      }
      else if (array.value.includes(item)) {
        array.value = array.value.filter(i => i !== item)
        resetPageAfterFilterChange()
      }
    }
  }

  function remover(array: Ref<string[]>) {
    return (item: string) => {
      if (array.value.includes(item)) {
        array.value = array.value.filter(i => i !== item)
        resetPageAfterFilterChange()
      }
    }
  }

  function toggleContentType(type: string, checked: boolean, singleSelect = false) {
    if (singleSelect) {
      const unchanged = checked
        ? selectedContentTypes.value.length === 1 && selectedContentTypes.value[0] === type
        : selectedContentTypes.value.length === 0
      if (!unchanged) {
        selectedContentTypes.value = checked ? [type] : []
        resetPageAfterFilterChange()
      }
    }
    else {
      toggler(selectedContentTypes)(type, checked)
    }
  }

  const toggleLegalCategory = (value: string, checked: boolean) => {
    toggler(selectedLegalCategories)(value, checked)
  }

  const toggleAuthor = (value: string, checked: boolean) => {
    toggler(selectedAuthors)(value, checked)
  }

  const filterContentType = remover(selectedContentTypes)
  const filterLegalCategory = remover(selectedLegalCategories)
  const filterAuthor = remover(selectedAuthors)

  function resetContentType() {
    if (selectedContentTypes.value.length) {
      selectedContentTypes.value = []
      resetPageAfterFilterChange()
    }
  }

  function resetFilters() {
    const routeFiltersChanged = selectedContentTypes.value.length > 0
      || selectedLegalCategories.value.length > 0
      || selectedAuthors.value.length > 0
      || startYearRoute.value !== undefined
      || endYearRoute.value !== undefined
      || sortOrder.value.value !== 'relevance'

    selectedContentTypes.value = []
    selectedLegalCategories.value = []
    selectedAuthors.value = []
    searchLegalCategory.value = ''
    searchAuthor.value = ''
    startYearRoute.value = undefined
    endYearRoute.value = undefined
    startYear.value = ''
    endYear.value = ''
    sortOrder.value = SORT_ORDER_OPTIONS[0]
    if (routeFiltersChanged) {
      resetPageAfterFilterChange()
    }
  }

  const filters = computed(() => {
    const start = startYearRoute.value
    const end = endYearRoute.value

    return {
      contentTypes: selectedContentTypes.value.flatMap(g => labelToNodeTypes[g] || []),
      legalAreas: selectedLegalCategories.value.map(area => extractId(area)).filter(Boolean),
      startYear: start && YEAR_PATTERN.test(start) ? start : undefined,
      endYear: end && YEAR_PATTERN.test(end) ? end : undefined,
      authors: selectedAuthors.value.map(author => extractId(author)).filter(Boolean),
    }
  })

  const sort = computed(() => {
    const sortValue = sortOrder.value.value
    if (sortValue === 'newest') {
      return 'timestamp DESC, lastUpdatedByEditor DESC, dateOfPublication DESC'
    }
    if (sortValue === 'oldest') {
      return 'timestamp ASC, lastUpdatedByEditor ASC, dateOfPublication ASC'
    }
    return ''
  })

  const totalFilters = computed(() => {
    return selectedContentTypes.value.length + selectedLegalCategories.value.length + selectedAuthors.value.length
  })

  return {
    selectedContentTypes,
    selectedLegalCategories,
    selectedAuthors,
    searchLegalCategory,
    searchAuthor,
    startYear,
    endYear,
    sortOrder,
    filters,
    sort,
    toggleContentType,
    toggleLegalCategory,
    toggleAuthor,
    filterContentType,
    resetContentType,
    filterLegalCategory,
    filterAuthor,
    resetFilters,
    updateYearFilter,
    totalFilters,
  }
}
